import { HttpHeaders } from '@angular/common/http';
import { AbstractControl, FormArray, FormGroup, UntypedFormArray, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { DialogService } from 'primeng/dynamicdialog';

import { ConfirmationModalComponent } from '../../../core/components/confirmation-modal/confirmation-modal.component';
import { LoanSummarySectionTypes } from '../../../loan-summary/models/loan-summary-section-types.enum';
import { CustomUiHeaders } from '../../../models';
import { IdValue } from '../../../shared/models/id-value.model';
import { InvalidControlModel } from '../../models';
import { Cmg1003SectionTypes, CreditSectionTypes } from '../../models/section-types.enum';

export class CmgUiUtilities {
  static getSectionName(section: Cmg1003SectionTypes): string {
    let sectionName = '';
    switch (section) {
      case Cmg1003SectionTypes.loanOfficer:
        sectionName = 'Loan Officer';
        break;
      case Cmg1003SectionTypes.borrowerInformation:
        sectionName = 'Borrower';
        break;
      case Cmg1003SectionTypes.loanInformation:
        sectionName = 'Loan';
        break;
      case Cmg1003SectionTypes.propertyInformation:
        sectionName = 'Property';
        break;
      case Cmg1003SectionTypes.employmentInformation:
        sectionName = 'Income';
        break;
      case Cmg1003SectionTypes.housingExpenses:
        sectionName = 'Combined Housing Exp';
        break;
      case Cmg1003SectionTypes.assetsCreditLiabilitiesInformation:
        sectionName = 'Assets & Liabilities';
        break;
      case Cmg1003SectionTypes.transactionDetails:
        sectionName = 'Cash To/From';
        break;
      case Cmg1003SectionTypes.declarations:
        sectionName = 'Declarations';
        break;
      case Cmg1003SectionTypes.gmiAndMilitaryServiceInformation:
        sectionName = 'GMI & Military';
        break;
      default:
        break;
    }
    return sectionName;
  }

  static getLoanSummarySectionName(section: LoanSummarySectionTypes): string {
    let sectionName = '';
    switch (section) {
      case LoanSummarySectionTypes.borrowerInformation:
        sectionName = 'Borrower';
        break;
      case LoanSummarySectionTypes.loanInformation:
        sectionName = 'Loan';
        break;
      case LoanSummarySectionTypes.propertyInformation:
        sectionName = 'Property';
        break;
      case LoanSummarySectionTypes.employmentInformation:
        sectionName = 'Income';
        break;
      case LoanSummarySectionTypes.liabilities:
        sectionName = 'Liabilities';
        break;
      default:
        break;
    }
    return sectionName;
  }

  static getCreditSectionName(section: CreditSectionTypes): string {
    let sectionName = '';
    switch (section) {
      case CreditSectionTypes.requestCreditReport:
        sectionName = 'Request Credit Report';
        break;
      case CreditSectionTypes.importLiabilities:
        sectionName = 'Import Liabilities';
        break;
      default:
        break;
    }
    return sectionName;
  }

  /**
   * Additional borrower Id is calculated by just negating thr primary borrower id.
   * @param primaryBorrowerId
   * @returns
   */
  static getTemporaryBorrowerId(primaryBorrowerId: number) {
    return primaryBorrowerId * -1;
  }

  /**
   * getUniqueNumber : this is unique enough for our needs, may be not in a for loop that runs 1000 times
   * @returns
   */
  static getUniqueNumber() {
    return Math.floor((Date.now() * Math.random()) / 10000);
  }

  /**
   * Encapsulates the logic to figure out if borrower is temporary.
   * This is needed because of evolving logic of temp borrowers.
   * @param borrowerId
   * @returns
   */
  static isIdTemporary(borrowerId: number) {
    return borrowerId < 0;
  }

  static getValidationMessageFromProperty(errorsObj: any, displayName: string, controlContext: string) {
    if (errorsObj.required === true) {
      let validationMessage = displayName ? `Please enter ${displayName}` : 'Required';
      if (controlContext) {
        validationMessage = `${controlContext} ${validationMessage}`;
      }
      return validationMessage;
    }
    return null;
  }

  static isElementVisible(el: HTMLElement) {
    const efp = function (x, y) {
        return document.elementFromPoint(x, y);
      },
      rect = el.getBoundingClientRect(),
      vHeight = window.innerHeight || document.documentElement.clientHeight,
      vWidth = window.innerWidth || document.documentElement.clientWidth;
    // Return false if it's not in the viewport
    if (rect.right < 0 || rect.bottom < 0 || rect.left > vWidth || rect.top > vHeight) {
      return false;
    }

    // Return true if any of its four corners are visible
    return (
      el.contains(efp(rect.left, rect.top)) ||
      el.contains(efp(rect.right, rect.top)) ||
      el.contains(efp(rect.right, rect.bottom)) ||
      el.contains(efp(rect.left, rect.bottom))
    );
  }

  /**
   * formGroup.markAsDirty() and formGroup.markAsTouched() do not work properly. This function
   * loops through each control as marks them as dirty and/or touched.
   * @param formGroup
   */
  static markFormDirtyTouched(formGroup: FormGroup | FormArray, dirty = true, touched = true, emitEvent = false): void {
    dirty ? formGroup.markAsDirty() : formGroup.markAsTouched();
    touched ? formGroup.markAsTouched() : formGroup.markAsUntouched();

    Object.keys(formGroup.controls).forEach((key: string) => {
      const abstractControl = formGroup.get(key);
      if (abstractControl instanceof FormGroup || abstractControl instanceof FormArray) {
        CmgUiUtilities.markFormDirtyTouched(abstractControl, dirty, touched);
      } else {
        dirty ? abstractControl.markAsDirty() : abstractControl.markAsPristine();
        touched ? abstractControl.markAsTouched() : abstractControl.markAsUntouched();

        try {
          abstractControl.updateValueAndValidity({ emitEvent: emitEvent });
        } catch {
          // Ignore error
        }
      }
    });

    formGroup.updateValueAndValidity({ emitEvent: emitEvent });
  }

  /**
   * formGroup.markAsDirty() does not work properly. This function
   * loops through each control as marks them as dirty.
   * @param formGroup
   */
  static markFormAsDirty(formGroup: UntypedFormGroup | UntypedFormArray, dirty = true, emitEvent = false) {
    Object.keys(formGroup?.controls).forEach((key: string) => {
      const abstractControl = formGroup?.get(key);
      if (abstractControl instanceof UntypedFormGroup || abstractControl instanceof UntypedFormArray) {
        CmgUiUtilities.markFormAsDirty(abstractControl, dirty);
      } else {
        dirty ? abstractControl.markAsDirty() : abstractControl.markAsPristine();

        try {
          (abstractControl as UntypedFormControl).updateValueAndValidity({ emitEvent: emitEvent });
        } catch {
          //
        }
      }
    });
    formGroup.updateValueAndValidity({ emitEvent: emitEvent });
  }

  /**
   * formGroup.markAsTouched() does not work properly. This function
   * loops through each control as marks them as touched.
   * @param formGroup
   */
  static markFormAsTouched(formGroup: UntypedFormGroup | UntypedFormArray, touched = true, emitEvent = false) {
    Object.keys(formGroup?.controls).forEach((key: string) => {
      const abstractControl = formGroup?.controls[key];
      if (abstractControl instanceof UntypedFormGroup || abstractControl instanceof UntypedFormArray) {
        CmgUiUtilities.markFormAsTouched(abstractControl);
      } else {
        touched ? (abstractControl as UntypedFormControl).markAsTouched() : (abstractControl as UntypedFormControl).markAsUntouched();

        try {
          (abstractControl as UntypedFormControl).updateValueAndValidity({ emitEvent: emitEvent });
        } catch {
          //
        }
      }
    });
    formGroup.updateValueAndValidity({ emitEvent: emitEvent });
  }

  /**
   * Reverse lookup of id from value
   * @param idValues
   * @param value
   * @returns
   */
  static idFromValue(idValues: IdValue[], value: string) {
    return idValues.find((t) => t.value === value)?.id;
  }

  static getControlsWithErrors(
    form: UntypedFormGroup | UntypedFormArray,
    controlErrors: { formControlName: string; abstractControl: AbstractControl }[] = undefined,
  ) {
    if (!controlErrors) {
      controlErrors = [];
    }

    if (form) {
      Object.keys(form.controls).forEach((key: string) => {
        const abstractControl = form.controls[key];
        if (abstractControl instanceof UntypedFormGroup || abstractControl instanceof UntypedFormArray) {
          CmgUiUtilities.getControlsWithErrors(abstractControl, controlErrors);
        } else {
          if (abstractControl && abstractControl.errors) {
            controlErrors.push({ formControlName: key, abstractControl: abstractControl });
          }
        }
      });
    }

    return controlErrors;
  }

  /**
   * Returns back only InvalidControlModels for which a matching element exists in the HTML,
   * because some "Required" controls aren't always visible
   */
  static getInvalidControlModels(controlErrors: { formControlName: string; abstractControl: AbstractControl }[]): InvalidControlModel[] {
    const invalidControls: InvalidControlModel[] = [];

    if (controlErrors && controlErrors.length > 0) {
      controlErrors.forEach((c) => {
        const htmlElement = (<any>c?.abstractControl)?.nativeElement;
        if (htmlElement) {
          const invalidControl = new InvalidControlModel();
          invalidControl.formControlName = c.formControlName;
          invalidControl.errors = c.abstractControl.errors;
          invalidControl.controlId = htmlElement.id;

          if (!invalidControl.controlId) {
            // If outer element does not have an id attribute defined, take id from first child.
            // Necessary due to issue with p-selects which are binding the supplied id on inner element and not the outer element.
            invalidControl.controlId = htmlElement.children[0]?.id;
          }

          invalidControls.push(invalidControl);
        }
      });
    }

    return invalidControls;
  }

  static sortLookup(list: IdValue<number | string>[], sortBy = 'value', addSelect = false) {
    return addSelect
      ? [new IdValue(0, 'Select'), ...list.sort((a, b) => (a[sortBy] > b[sortBy] ? 1 : b[sortBy] > a[sortBy] ? -1 : 0))]
      : [...list.sort((a, b) => (a[sortBy] > b[sortBy] ? 1 : b[sortBy] > a[sortBy] ? -1 : 0))];
  }

  static isAppRunningInIframe() {
    return window.self !== window.top;
  }

  static convertToNumber(strValue: string): number {
    return isNaN(Number(strValue)) ? 0 : Number(strValue);
  }

  /**
   * Prefix returned by this method is displayed in the AUS SlideOutPanel before warnings that are borrower specific.
   * Needed because borrower first name/last name are not guaranteed to be entered on Borrower Information section, before changes to other sections are made.
   * @param isPrimaryBorrower
   * @param firstName
   * @param lastName
   * @param suffix
   * @returns
   */
  static getBorrowerNameErrorPrefix(isPrimaryBorrower: boolean, firstName?: string, lastName?: string, suffix?: string) {
    let errorPrefix = '';

    if (firstName || lastName) {
      errorPrefix = `${firstName} ${lastName} ${suffix}`.trim();
    } else if (isPrimaryBorrower) {
      errorPrefix = 'Primary Borrower';
    } else {
      errorPrefix = 'Additional Borrower';
    }

    return errorPrefix + ': ';
  }

  static goToControl(invalidControl: InvalidControlModel, validationGlow = true) {
    const htmlCtrl = document.getElementById(invalidControl.controlId);
    if (htmlCtrl) {
      htmlCtrl.tabIndex = 0;
      // If scrollTo does not work, check whether the element has a tabindex defined. For some reason that seems to be necessary.
      // scrollIntoView has issues in Chrome when setting behavior to smooth so leave it set to auto for now
      htmlCtrl.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'end' });

      if (validationGlow) {
        htmlCtrl.classList.add('validation-glow');
        setTimeout(() => {
          htmlCtrl.classList.remove('validation-glow');
        }, 6000);
      }

      setTimeout(
        (ctrl: HTMLElement) => {
          ctrl.focus();
          htmlCtrl.tabIndex = undefined;
        },
        100 /* 100 ms seems to work always */,
        htmlCtrl,
      );
    }
  }

  static getSkipSpinnerHeaders() {
    return new HttpHeaders().set(CustomUiHeaders.skipSpinner, 'true' /* value doesn't matter, just for semantic purposes */);
  }

  static getSkipAllSpinnerHeaders() {
    return new HttpHeaders()
      .set(CustomUiHeaders.skipSpinner, 'true' /* value doesn't matter, just for semantic purposes */)
      .set(CustomUiHeaders.skipResetSpinner, 'true');
  }

  static showUnsavedChangesModal(dialogService: DialogService, message: string, acceptLabel: string, rejectLabel: string, closable: boolean) {
    return dialogService.open(ConfirmationModalComponent, {
      draggable: true,
      keepInViewport: true,
      closable,
      width: '450px',
      data: {
        message,
        header: 'Unsaved Changes',
        icon: 'pi pi-save',
        acceptLabel,
        rejectLabel,
      },
    });
  }
}
